import React, { useEffect, useState } from 'react';
import { Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { PlusOutlined } from '@ant-design/icons';

import { BASE_API_URL, BASE_IMAGE_URL } from '../env';
import { getToken } from '../service/CookieManager';
import { toastError, toastSuccess } from '../service/Toast';

type PartnerLogoUploadProps = {
  value?: string;
  onChange?: (url: string | undefined) => void;
};

const uploadAction = `${BASE_API_URL}/documitra-content/upload`;
const uploadHeaders = {
  Authorization: `Bearer ${getToken(`${BASE_API_URL}/utils/fileUpload`)}`,
};

function toPreviewUrl(path?: string) {
  if (!path) return path;
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('blob:')) {
    return path;
  }
  const base = (BASE_IMAGE_URL || '').replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

const PartnerLogoUpload: React.FC<PartnerLogoUploadProps> = ({ value, onChange }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  useEffect(() => {
    if (value) {
      setFileList([
        {
          uid: '-1',
          name: 'partner-logo',
          status: 'done',
          url: toPreviewUrl(value),
        },
      ]);
      return;
    }
    setFileList([]);
  }, [value]);

  return (
    <Upload
      name="iconImage"
      listType="picture-card"
      fileList={fileList}
      maxCount={1}
      accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
      action={uploadAction}
      headers={uploadHeaders}
      beforeUpload={(file) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
          toastError('Please upload an image file');
          return Upload.LIST_IGNORE;
        }
        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
          toastError('Image must be smaller than 2MB');
          return Upload.LIST_IGNORE;
        }
        return true;
      }}
      onChange={(info) => {
        const nextList = info.fileList.slice(-1).map((f) => {
          if (f.status === 'done' && f.response?.data && !f.url) {
            f.url = toPreviewUrl(f.response.data as string);
          }
          return f;
        });
        setFileList(nextList);

        const file = info.file;
        if (file.status === 'done') {
          const url =
            file.url ||
            (file.response?.data as string | undefined) ||
            (typeof file.response === 'string' ? file.response : undefined);
          if (url) {
            onChange?.(url);
            toastSuccess('Image uploaded');
          }
        } else if (file.status === 'error') {
          toastError('Image upload failed');
        } else if (file.status === 'removed') {
          onChange?.(undefined);
        }
      }}
      onRemove={() => {
        onChange?.(undefined);
        return true;
      }}
    >
      {fileList.length >= 1 ? null : (
        <div>
          <PlusOutlined />
          <div style={{ marginTop: 8 }}>Upload image</div>
        </div>
      )}
    </Upload>
  );
};

export default PartnerLogoUpload;
